import React from 'react';
import { Col, Flex, Row, Tag } from 'antd';
import {
  BatteryIcon,
  HumidityIcon,
  TempratureRangeIcon,
} from '../../../assets/icons/icons';
import { CustomParagraph } from '../../../components';
import { GoogleMapsProvider } from '../../../components/GoogleMapsProvider';
import ShipmentMap from '../ShipmentMap';
import { SingleShipmentDataExtended } from '@/apis/types';
import {
  buildStatusPillStyle,
  getDeliveryStatusPillColors,
} from '../statusTagColors';

interface Location {
  name?: string;
}

interface MonitorInfo {
  temperature?: string;
  battery?: string;
  humidity?: string;
}

interface Product {
  name?: string;
  product_type?: string;
}

interface Sensor {
  type?: string;
  id?: string;
}

interface Courier {
  title?: string;
}

interface Shipment {
  reference_number?: string;
  delivery_status?: string;
  estimated_delivery_date?: string;
  monitor_info: MonitorInfo;
  from_location?: Location;
  to_location?: Location;
  scheduled_pickup_date?: string;
  scheduled_delivery_date?: string;
  weight_kg?: string;
  volume?: string;
  product: Product;
  sensor: Sensor;
  courier: Courier;
  contact_phone?: string;
  courier_tracking_id?: string;
  transport_provider_eta?: string;
}

interface ShipmentPreviewProps {
  shipment: Shipment;
}

/**
 * Preview card component for displaying shipment details
 *
 * @param shipment - The shipment data to display
 */
const ShipmentPreview: React.FC<ShipmentPreviewProps> = ({ shipment }) => {
  const lastPoint = { lat: 40.7128, lng: -74.006 };
  const deliveryStatusColors = getDeliveryStatusPillColors(
    shipment.delivery_status
  );

  const renderTag = (
    _label: string,
    value?: string,
    icon?: React.ReactNode,
    style?: React.CSSProperties
  ) => (
    <Tag
      icon={icon}
      style={{
        height: 20,
        borderRadius: 4,
        padding: '0 6px',
        paddingTop: 3,
        fontWeight: 600,
        ...style,
        border: 'none',
      }}
    >
      {value}
    </Tag>
  );

  return (
    <Flex
      vertical
      gap={20}
      style={{
        borderRadius: '12px',
        border: '2px solid #EEE',
        padding: '30px 35px',
        cursor: 'pointer',
        height: 'fit-content',
      }}
    >
      <Flex align='center' gap={8}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 24,
            color: 'var(--text-primary)',
          }}
        >
          {shipment.reference_number}
        </div>
      </Flex>

      <Row justify='space-between'>
        <Col>
          <Tag
            style={buildStatusPillStyle(deliveryStatusColors, {
              fontSize: '10px',
              fontWeight: 600,
              borderRadius: 8,
            })}
          >
            {shipment.delivery_status}
          </Tag>
        </Col>
        <Col>
          <CustomParagraph
            text='Zoomlogi ETA'
            marginBottom={0}
            fontSize='12px'
            fontWeight={600}
          />
          <CustomParagraph
            text={shipment.estimated_delivery_date}
            fontSize='12px'
          />
        </Col>
      </Row>

      <GoogleMapsProvider>
        <ShipmentMap
          currentView='summary'
          lastCoordinates={lastPoint}
          // TODO: this cast is unsafe
          shipment={shipment as SingleShipmentDataExtended}
          height={'240px'}
          isLargeScreen={false}
          onPointClicked={() => {}}
        />
      </GoogleMapsProvider>

      <Flex justify='space-between' align='center' style={{ marginBottom: 8 }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 18,
            color: 'var(--text-primary)',
          }}
        >
          Shipment details
        </div>
        <Flex gap={12} wrap>
          {renderTag(
            'Temperature',
            shipment.monitor_info.temperature,
            <TempratureRangeIcon />,
            {
              backgroundColor: 'var(--light-gray)',
              color: '#757575',
            }
          )}
          {renderTag(
            'Battery',
            shipment.monitor_info.battery,
            <BatteryIcon />,
            {
              backgroundColor: 'var(--light-gray)',
              color: '#757575',
            }
          )}
          {renderTag(
            'Humidity',
            shipment.monitor_info.humidity,
            <HumidityIcon />,
            {
              backgroundColor: 'var(--light-gray)',
              color: '#757575',
            }
          )}
        </Flex>
      </Flex>

      <Row justify='space-between'>
        <Col span={11}>
          <CustomParagraph
            text='Origin'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment?.from_location?.name}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
        <Col span={11}>
          <CustomParagraph
            text='Destination'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment?.to_location?.name}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
      </Row>

      <Row justify='space-between'>
        <Col span={11}>
          <CustomParagraph
            text='Pickup Date'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment.scheduled_pickup_date}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
        <Col span={11}>
          <CustomParagraph
            text='Delivery Date'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment.scheduled_delivery_date}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
      </Row>

      <Row justify='space-between'>
        <Col span={11}>
          <CustomParagraph
            text='Weight (kg)'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment.weight_kg}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
        <Col span={11}>
          <CustomParagraph
            text='Dimensions'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment.volume}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
      </Row>

      <Row justify='space-between'>
        <Col span={11}>
          <CustomParagraph
            text='Product Name'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment.product.name}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
        <Col span={11}>
          <CustomParagraph
            text='Product Type'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment.product.product_type}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
      </Row>

      <Row justify='space-between'>
        <Col span={11}>
          <CustomParagraph
            text='Sensor Type'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment.sensor.type}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
        <Col span={11}>
          <CustomParagraph
            text='Sensor ID'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment.sensor.id}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
      </Row>

      <Row justify='space-between'>
        <Col span={11}>
          <CustomParagraph
            text='Courier'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment.courier.title}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
        <Col span={11}>
          <CustomParagraph
            text='Phone'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment.contact_phone}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
      </Row>

      <Row justify='space-between'>
        <Col span={11}>
          <CustomParagraph
            text='Tracking ID'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment.courier_tracking_id}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
        <Col span={11}>
          <CustomParagraph
            text='ETA'
            fontSize='12px'
            fontWeight={600}
            marginBottom={0}
          />
          <CustomParagraph
            text={shipment.transport_provider_eta}
            fontSize='12px'
            marginBottom={0}
          />
        </Col>
      </Row>
    </Flex>
  );
};

export default ShipmentPreview;
